import { useQuery } from "@tanstack/react-query";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const providersKey = ["auth", "providers"] as const;

/**
 * Asked of the API, not baked in: one bundle is promoted acc to production (ADR-0005), so an
 * unconfigured provider must simply not appear.
 */
export function useProviders() {
  return useQuery({
    queryKey: providersKey,
    queryFn: async () => {
      const { data, response } = await api.GET("/api/auth/providers");
      if (!data)
        throw new Error(
          i18n.t("session:errors.providersUnavailable", { status: response.status }),
        );
      return data.schemes ?? [];
    },
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * `SignIn` renders only what is named here, so the other door's schemes get no dead button. Not
 * a gate - the API refuses a challenge for a scheme this host does not offer - but in development
 * both dev servers answer to both loopback names. See docs/runbook/oauth-clients.md.
 */
const PROVIDER_SCHEMES = ["microsoft-backoffice"] as const;

export type ProviderScheme = (typeof PROVIDER_SCHEMES)[number];

/** Whether this host's answer is a scheme `SignIn` knows how to draw. */
export const isKnownProvider = (scheme: string): scheme is ProviderScheme =>
  (PROVIDER_SCHEMES as readonly string[]).includes(scheme);

/**
 * Read at render rather than held in a module constant: a constant would be built before
 * i18next has initialised and would freeze whatever the catalog said then.
 */
export const providerLabel = (scheme: ProviderScheme) =>
  i18n.t(`session:signIn.providers.${scheme}` as const);
